import {
  CLAVES_DE_FILTRO,
  DIMENSIONES_INVENTARIO,
  ETIQUETA_DIMENSION,
  ETIQUETA_DIMENSION_EN,
  ETIQUETA_MEDIDA,
  FILTROS_DE_COLUMNA_INVENTARIO,
  MEDIDAS_INVENTARIO,
  NOMBRE_MEDIDA_EN,
  type AgregadoDeInventario,
  type ConfiguracionDeVista,
  type ConsultaAgregado,
  type DimensionInventario,
  type MedidaInventario,
} from '@cnc/contracts';
import {useCallback, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  consultaDeReporteDesdeParametros,
  parametrosDesdeConsulta,
  soloFiltros,
  urlDeExcelDelReporte,
  useAgregado,
  useFiltrosDeInventario,
  useOpcionesDeColumnaDeInventario,
} from '../api/inventario';
import {BarraDeVistas} from '../componentes/barra-de-vistas';
import {COLUMNAS_DE_INVENTARIO, DEFINICION_INVENTARIO} from '../componentes/columnas-inventario';
import {FiltrosInventario} from '../componentes/filtros-inventario';
import {FiltrosPorColumna} from '../componentes/filtros-por-columna';
import {useColumnas} from '../componentes/usar-columnas';
import {columnasConFiltro, type UseOpcionesDeColumna} from '../utilidades/columnas-con-filtro';
import {AYUDA_DIMENSION, AYUDA_MEDIDA, AYUDA_REPORTES} from '../componentes/ayuda-general';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PestanasAnalisis} from '../componentes/pestanas-analisis';
import {et, localeActual, tr, trx, txt} from '../i18n/idioma';
import {mes, numero} from '../utilidades/formato';

// Tonos de la marca Sun Valley (bronce, oro, café) y otros bien distintos
// para que cada serie se reconozca.
const COLORES = [
  '#8a5a2b',
  '#c9963f',
  '#3a2313',
  '#4f6d8f',
  '#2f6b3a',
  '#a33a16',
  '#8a8f98',
  '#6b7a2f',
  '#2d6f73',
  '#7a4b6b',
];
const DIMENSIONES_DE_TIEMPO: DimensionInventario[] = ['mesMolienda', 'mesEmpaque', 'mesEtd', 'anioEtd'];
const SIN_DATO = txt('(sin dato)', '(no data)');
// Clave del total de cada barra en los datos de la grafica (no es una serie).
const TOTAL_DE_BARRA = '__total';

// Nombres de dimensiones y medidas en el idioma activo.
const nombreDimension = (d: DimensionInventario) => et(ETIQUETA_DIMENSION, ETIQUETA_DIMENSION_EN)[d];
const nombreMedida = (m: MedidaInventario) => tr(ETIQUETA_MEDIDA[m].nombre, NOMBRE_MEDIDA_EN[m]);

type TipoDeGrafica = 'barras' | 'lineas';

// Reportes del inventario: tabla dinamica (filas × columnas × medidas),
// grafica y descarga a Excel, con los mismos filtros de la tabla.
export function PaginaReportes() {
  const [parametros, setParametros] = useSearchParams();
  const consulta = consultaDeReporteDesdeParametros(parametros);
  const [medidaDeGrafica, setMedidaDeGrafica] = useState<MedidaInventario>(consulta.medidas[0] ?? 'bigBags');
  const [tipoDeGrafica, setTipoDeGrafica] = useState<TipoDeGrafica>(
    DIMENSIONES_DE_TIEMPO.includes(consulta.filas) ? 'lineas' : 'barras',
  );
  const agregado = useAgregado(consulta);
  const filtros = useFiltrosDeInventario(soloFiltros(consulta));
  // Filtros por columna: los de las columnas elegidas en la tabla del inventario.
  const [columnasDelInventario] = useColumnas(DEFINICION_INVENTARIO);
  const useOpciones: UseOpcionesDeColumna = (columna, contiene, habilitado) =>
    useOpcionesDeColumnaDeInventario(soloFiltros(consulta), columna, contiene, habilitado);
  const medidaActiva: MedidaInventario = consulta.medidas.includes(medidaDeGrafica)
    ? medidaDeGrafica
    : (consulta.medidas[0] ?? 'bigBags');

  const cambiar = useCallback(
    (cambios: Partial<ConsultaAgregado>) => {
      const actual = consultaDeReporteDesdeParametros(new URLSearchParams(window.location.search));
      setParametros(parametrosDesdeConsulta({...actual, ...cambios}), {replace: true});
    },
    [setParametros],
  );

  // Abrir una vista guardada: tabla dinamica y filtros a la direccion, grafica a la pantalla.
  const aplicarVista = useCallback(
    (configuracion: ConfiguracionDeVista) => {
      setParametros(new URLSearchParams(configuracion.parametros), {replace: true});
      const {grafica, medidaGrafica} = configuracion.extra;
      if (grafica === 'barras' || grafica === 'lineas') setTipoDeGrafica(grafica);
      if (typeof medidaGrafica === 'string' && (MEDIDAS_INVENTARIO as readonly string[]).includes(medidaGrafica))
        setMedidaDeGrafica(medidaGrafica as MedidaInventario);
    },
    [setParametros],
  );

  const alternarMedida = (medida: MedidaInventario) => {
    const actuales = consulta.medidas;
    const nuevas = actuales.includes(medida) ? actuales.filter(m => m !== medida) : [...actuales, medida];
    if (nuevas.length) cambiar({medidas: MEDIDAS_INVENTARIO.filter(m => nuevas.includes(m))});
  };

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Reportes', 'Pivot reports')}</h1>
        <p className="text-sm text-apagado">
          {tr(
            'Tablas dinámicas y gráficas sobre los Big Bags que cumplen los filtros.',
            'Pivot tables and charts for the Big Bags that match the filters.',
          )}
        </p>
      </div>
      <PestanasAnalisis />

      <BarraDeVistas
        pantalla="reportes"
        actual={{
          parametros: parametros.toString(),
          columnas: null,
          extra: {grafica: tipoDeGrafica, medidaGrafica: medidaActiva},
        }}
        onAplicar={aplicarVista}
      />

      <FiltrosInventario
        consulta={consulta}
        filtros={filtros.data}
        onCambiar={cambiar}
        onLimpiar={() => cambiar(Object.fromEntries(CLAVES_DE_FILTRO.map(k => [k, undefined])))}
        // Las mismas columnas que la persona agrego a la tabla del inventario.
        porColumna={
          <FiltrosPorColumna
            columnas={columnasConFiltro(
              columnasDelInventario,
              COLUMNAS_DE_INVENTARIO,
              FILTROS_DE_COLUMNA_INVENTARIO,
              consulta.porColumna,
            )}
            valor={consulta.porColumna}
            onCambiar={porColumna => cambiar({porColumna})}
            useOpciones={useOpciones}
            ayuda={tr(
              'Uno por cada columna elegida en la tabla del Inventario.',
              'One per column chosen in the Inventory table.',
            )}
          />
        }
      />

      <div className="grid gap-3 rounded-lg border border-linea bg-white p-3 lg:grid-cols-[1fr_1fr_2fr]">
        <label className="block text-xs text-apagado">
          <span>
            {tr('Filas', 'Rows')}
            <NotaDeAyuda texto={AYUDA_REPORTES.filas} dentroDeEtiqueta />
          </span>
          <select
            value={consulta.filas}
            onChange={e => cambiar({filas: e.target.value as DimensionInventario})}
            className="mt-1 w-full rounded-md border border-linea bg-white px-2 py-1.5 text-sm text-tinta"
          >
            {DIMENSIONES_INVENTARIO.map(d => (
              <option key={d} value={d}>
                {nombreDimension(d)}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-apagado">
          <span>
            {tr('Columnas', 'Columns')}
            <NotaDeAyuda texto={AYUDA_REPORTES.columnas} dentroDeEtiqueta />
          </span>
          <select
            value={consulta.columnas ?? ''}
            onChange={e => cambiar({columnas: (e.target.value || undefined) as DimensionInventario | undefined})}
            className="mt-1 w-full rounded-md border border-linea bg-white px-2 py-1.5 text-sm text-tinta"
          >
            <option value="">{tr('Ninguna', 'None')}</option>
            {DIMENSIONES_INVENTARIO.filter(d => d !== consulta.filas).map(d => (
              <option key={d} value={d}>
                {nombreDimension(d)}
              </option>
            ))}
          </select>
        </label>
        <div className="text-xs text-apagado">
          {tr('Medidas', 'Measures')}
          <NotaDeAyuda texto={AYUDA_REPORTES.medidas} />
          <div className="mt-1 flex flex-wrap gap-1.5">
            {MEDIDAS_INVENTARIO.map(m => {
              const activa = consulta.medidas.includes(m);
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => alternarMedida(m)}
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    activa
                      ? 'border-verde bg-verde-claro text-verde-oscuro'
                      : 'border-linea bg-white text-apagado hover:text-tinta'
                  }`}
                  aria-pressed={activa}
                >
                  {nombreMedida(m)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {agregado.isError && (
        <div className="rounded-lg border border-alerta/30 bg-red-50 p-4 text-sm">
          <div className="font-medium text-alerta">
            {tr('No se pudo armar el reporte', 'Could not build the report')}
          </div>
          <div className="mt-1">{agregado.error.message}</div>
        </div>
      )}

      {agregado.data && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-apagado">
              {tr(
                `${numero(agregado.data.bigBags)} Big Bags en el reporte`,
                `${numero(agregado.data.bigBags)} Big Bags in the report`,
              )}
              {agregado.data.truncado &&
                tr(
                  ' · el reporte tiene demasiadas combinaciones y se recortó; usa más filtros',
                  ' · the report has too many combinations and was truncated; use more filters',
                )}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-apagado">
                {tr('Gráfica', 'Chart')}
                <NotaDeAyuda texto={AYUDA_REPORTES.grafica} dentroDeEtiqueta />
                <select
                  value={tipoDeGrafica}
                  onChange={e => setTipoDeGrafica(e.target.value as TipoDeGrafica)}
                  className="ml-1 rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta"
                >
                  <option value="barras">{tr('Barras', 'Bars')}</option>
                  <option value="lineas">{tr('Líneas', 'Lines')}</option>
                </select>
              </label>
              <label className="text-xs text-apagado">
                {tr('Medida', 'Measure')}
                <NotaDeAyuda texto={AYUDA_REPORTES.medida} dentroDeEtiqueta />
                <select
                  value={medidaActiva}
                  onChange={e => setMedidaDeGrafica(e.target.value as MedidaInventario)}
                  className="ml-1 rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta"
                >
                  {consulta.medidas.map(m => (
                    <option key={m} value={m}>
                      {nombreMedida(m)}
                    </option>
                  ))}
                </select>
              </label>
              <a
                href={urlDeExcelDelReporte(consulta)}
                className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro"
                download
              >
                {tr('Descargar en Excel', 'Download to Excel')}
              </a>
            </div>
          </div>
          <Grafica agregado={agregado.data} medida={medidaActiva} tipo={tipoDeGrafica} />
          <TablaDinamica agregado={agregado.data} medida={medidaActiva} />
        </>
      )}
      {agregado.isPending && <p className="text-apagado">{tr('Armando el reporte…', 'Building the report…')}</p>}
    </section>
  );
}

const etiquetaDeValor = (dimension: DimensionInventario, valor: string) =>
  valor === ''
    ? trx(SIN_DATO)
    : DIMENSIONES_DE_TIEMPO.includes(dimension) && dimension !== 'anioEtd'
      ? mes(valor)
      : valor;

function usePivote(agregado: AgregadoDeInventario, medida: MedidaInventario) {
  return useMemo(() => {
    const filas = [...new Set(agregado.celdas.map(c => c.fila))];
    const columnas = agregado.columnas ? [...new Set(agregado.celdas.map(c => c.columna ?? ''))] : [];
    const valor = new Map<string, number>();
    for (const c of agregado.celdas) valor.set(`${c.fila}\u0000${c.columna ?? ''}`, c.valores[medida] ?? 0);
    const totalFila = new Map<string, Record<MedidaInventario, number>>();
    for (const c of agregado.celdas) {
      const acumulado = totalFila.get(c.fila) ?? ({} as Record<MedidaInventario, number>);
      for (const m of agregado.medidas) acumulado[m] = (acumulado[m] ?? 0) + (c.valores[m] ?? 0);
      totalFila.set(c.fila, acumulado);
    }
    const totalColumna = new Map<string, number>();
    for (const c of agregado.celdas)
      totalColumna.set(c.columna ?? '', (totalColumna.get(c.columna ?? '') ?? 0) + (c.valores[medida] ?? 0));
    return {filas, columnas, valor, totalFila, totalColumna};
  }, [agregado, medida]);
}

// Los promedios y porcentajes no se suman entre celdas: el total de una fila
// con columnas solo se muestra para las medidas que sí se suman.
const SE_SUMA: Record<MedidaInventario, boolean> = {
  bigBags: true,
  tonHumedas: true,
  tonSecas: true,
  humedadTon: true,
  humedadPorcentaje: false,
  auG: true,
  agG: true,
  auOz: true,
  agOz: true,
  auGtPromedio: false,
  agGtPromedio: false,
};

function TablaDinamica({agregado, medida}: {agregado: AgregadoDeInventario; medida: MedidaInventario}) {
  const pivote = usePivote(agregado, medida);
  const decimales = (m: MedidaInventario) => ETIQUETA_MEDIDA[m].decimales;
  const cabecera =
    'border-b border-linea px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-apagado whitespace-nowrap';
  const celda = 'border-b border-linea/70 px-3 py-1.5 text-right tabular-nums whitespace-nowrap';

  if (!agregado.celdas.length)
    return (
      <div className="rounded-lg border border-linea bg-white p-6 text-center text-apagado">
        {tr('No hay Big Bags que cumplan estos filtros.', 'No Big Bags match these filters.')}
      </div>
    );

  if (!agregado.columnas) {
    return (
      <div className="overflow-auto rounded-lg border border-linea bg-white">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead className="bg-papel">
            <tr>
              <th className={cabecera}>
                {nombreDimension(agregado.filas)}
                <NotaDeAyuda texto={AYUDA_DIMENSION[agregado.filas]} />
              </th>
              {agregado.medidas.map(m => (
                <th key={m} className={`${cabecera} text-right`}>
                  {nombreMedida(m)}
                  <NotaDeAyuda texto={AYUDA_MEDIDA[m]} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pivote.filas.map(fila => {
              const c = agregado.celdas.find(x => x.fila === fila)!;
              return (
                <tr key={fila} className="hover:bg-verde-claro/40">
                  <td className="border-b border-linea/70 px-3 py-1.5 whitespace-nowrap">
                    {etiquetaDeValor(agregado.filas, fila)}
                  </td>
                  {agregado.medidas.map(m => (
                    <td key={m} className={celda}>
                      {numero(c.valores[m], decimales(m))}
                    </td>
                  ))}
                </tr>
              );
            })}
            <tr className="bg-papel font-semibold">
              <td className="px-3 py-1.5">Total</td>
              {agregado.medidas.map(m => (
                <td key={m} className="px-3 py-1.5 text-right tabular-nums">
                  {numero(agregado.totales[m], decimales(m))}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded-lg border border-linea bg-white">
      <div className="px-3 pt-2 text-xs text-apagado">
        {tr(
          `${nombreMedida(medida)} por ${nombreDimension(agregado.filas).toLowerCase()} y ${nombreDimension(agregado.columnas).toLowerCase()}`,
          `${nombreMedida(medida)} by ${nombreDimension(agregado.filas).toLowerCase()} and ${nombreDimension(agregado.columnas).toLowerCase()}`,
        )}
      </div>
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead className="bg-papel">
          <tr>
            <th className={cabecera}>
              {nombreDimension(agregado.filas)}
              <NotaDeAyuda texto={AYUDA_DIMENSION[agregado.filas]} />
            </th>
            {pivote.columnas.map(col => (
              <th key={col} className={`${cabecera} text-right`}>
                {etiquetaDeValor(agregado.columnas!, col)}
              </th>
            ))}
            {SE_SUMA[medida] && <th className={`${cabecera} text-right`}>Total</th>}
          </tr>
        </thead>
        <tbody>
          {pivote.filas.map(fila => (
            <tr key={fila} className="hover:bg-verde-claro/40">
              <td className="border-b border-linea/70 px-3 py-1.5 whitespace-nowrap">
                {etiquetaDeValor(agregado.filas, fila)}
              </td>
              {pivote.columnas.map(col => {
                const v = pivote.valor.get(`${fila}\u0000${col}`);
                return (
                  <td key={col} className={celda}>
                    {v === undefined ? '' : numero(v, decimales(medida))}
                  </td>
                );
              })}
              {SE_SUMA[medida] && (
                <td className={`${celda} font-semibold`}>
                  {numero(pivote.totalFila.get(fila)?.[medida] ?? 0, decimales(medida))}
                </td>
              )}
            </tr>
          ))}
          {SE_SUMA[medida] && (
            <tr className="bg-papel font-semibold">
              <td className="px-3 py-1.5">Total</td>
              {pivote.columnas.map(col => (
                <td key={col} className="px-3 py-1.5 text-right tabular-nums">
                  {numero(pivote.totalColumna.get(col) ?? 0, decimales(medida))}
                </td>
              ))}
              <td className="px-3 py-1.5 text-right tabular-nums">
                {numero(agregado.totales[medida], decimales(medida))}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Grafica({
  agregado,
  medida,
  tipo,
}: {
  agregado: AgregadoDeInventario;
  medida: MedidaInventario;
  tipo: TipoDeGrafica;
}) {
  const pivote = usePivote(agregado, medida);
  const datos = pivote.filas.map(fila => {
    const punto: Record<string, string | number> = {nombre: etiquetaDeValor(agregado.filas, fila)};
    let total = 0;
    if (agregado.columnas) {
      for (const col of pivote.columnas) {
        const v = pivote.valor.get(`${fila}\u0000${col}`) ?? 0;
        punto[etiquetaDeValor(agregado.columnas, col)] = v;
        total += v;
      }
    } else total = punto.valor = pivote.valor.get(`${fila}\u0000`) ?? 0;
    // Total de la barra (la pila completa cuando hay columnas), para mostrarlo encima.
    punto[TOTAL_DE_BARRA] = total;
    return punto;
  });
  const series = agregado.columnas ? pivote.columnas.map(col => etiquetaDeValor(agregado.columnas!, col)) : ['valor'];
  const decimales = ETIQUETA_MEDIDA[medida].decimales;
  const formato = (v: unknown) => numero(typeof v === 'number' ? v : Number(v), decimales);
  // Valor corto sobre las barras (1,2 mil) para que quepa.
  const corto = new Intl.NumberFormat(localeActual(), {notation: 'compact', maximumFractionDigits: 1});
  if (!datos.length) return null;
  const muchasFilas = datos.length > 40;
  const propiedadesComunes = {data: datos, margin: {top: 8, right: 16, bottom: muchasFilas ? 72 : 24, left: 8}};
  const ejes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-linea)" />
      <XAxis
        dataKey="nombre"
        tick={{fontSize: 11}}
        interval={muchasFilas ? 'preserveStartEnd' : 0}
        angle={muchasFilas ? -45 : 0}
        textAnchor={muchasFilas ? 'end' : 'middle'}
        height={muchasFilas ? 80 : 30}
      />
      <YAxis tick={{fontSize: 11}} tickFormatter={v => numero(Number(v), 0)} width={72} />
      <Tooltip formatter={formato} />
      {agregado.columnas && <Legend />}
    </>
  );
  return (
    <div className="rounded-lg border border-linea bg-white p-3">
      <div className="text-xs text-apagado">
        {tr(
          `${nombreMedida(medida)} por ${nombreDimension(agregado.filas).toLowerCase()}`,
          `${nombreMedida(medida)} by ${nombreDimension(agregado.filas).toLowerCase()}`,
        )}
        {agregado.columnas
          ? tr(
              `, dividido por ${nombreDimension(agregado.columnas).toLowerCase()}`,
              `, split by ${nombreDimension(agregado.columnas).toLowerCase()}`,
            )
          : ''}
      </div>
      <div style={{height: 360}}>
        <ResponsiveContainer width="100%" height="100%">
          {tipo === 'lineas' ? (
            <LineChart {...propiedadesComunes}>
              {ejes}
              {series.map((s, i) => (
                <Line
                  key={s}
                  type="monotone"
                  dataKey={s}
                  name={s === 'valor' ? nombreMedida(medida) : s}
                  stroke={COLORES[i % COLORES.length]}
                  dot={datos.length <= 40}
                  strokeWidth={2}
                />
              ))}
            </LineChart>
          ) : (
            <BarChart {...propiedadesComunes} margin={{...propiedadesComunes.margin, top: 20}}>
              {ejes}
              {series.map((s, i) => (
                <Bar
                  key={s}
                  dataKey={s}
                  name={s === 'valor' ? nombreMedida(medida) : s}
                  stackId={agregado.columnas ? 'apilado' : undefined}
                  fill={COLORES[i % COLORES.length]}
                >
                  {/* El valor de la barra (o de la pila completa) encima de la ultima serie. */}
                  {i === series.length - 1 && !muchasFilas && SE_SUMA[medida] && (
                    <LabelList
                      dataKey={TOTAL_DE_BARRA}
                      position="top"
                      formatter={(v: unknown) => (typeof v === 'number' && v !== 0 ? corto.format(v) : '')}
                      style={{fontSize: 10, fill: 'var(--color-tinta)'}}
                    />
                  )}
                  {i === series.length - 1 && !muchasFilas && !SE_SUMA[medida] && !agregado.columnas && (
                    <LabelList dataKey="valor" position="top" formatter={formato} style={{fontSize: 10}} />
                  )}
                </Bar>
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
